import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import InvestorPagedLookupCriteria from '../Models/Investors/Lookups/InvestorPagedLookupCriteria';
import InvestorPagedLookup from '../Models/Investors/Lookups/InvestorPagedLookup';
import Investor from '../Models/Investors/Investor';
import InvestorLookup from '../Models/Investors/Lookups/InvestorLookup';

export interface IInvestorApiClient {

    /**
     * Gets the investor for a given investor id
     * @param investorId The investor's Id
     * @returns A
     */
    getInvestor(investorId: number): AxiosPromise<Model.PlainTrackedObject<Investor>>;

    /**
     * Gets a paged list of investor lookups
     * @param request The page request containing filter criteria
     * @returns A paged list of
     */
    getInvestorLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<InvestorPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InvestorPagedLookup>>>;

    /**
     * Gets a list of investors that match the search text. Limited to 50 results.
     */
    getInvestorLookups(searchText: string): AxiosPromise<Array<Model.PlainObject<InvestorLookup>>>;

    /** 
     * Gets an investor that matches the id.
     */
    getInvestorLookup(investorId: number): AxiosPromise<Model.PlainObject<InvestorLookup>>;

    /**
     * Saves an investor
     * @param investor Investor to be saved
     * @returns A  representing the result of the asynchronous operation.
     */
    saveInvestor(investor: Model.PartialPlainObject<Investor>): AxiosPromise<Model.PlainTrackedObject<Investor>>;

    // Client only properties / methods
}

@injectable()
export default class InvestorApiClient extends Data.ApiClientBase implements IInvestorApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/investor`);
    }

    public getInvestor(investorId: number): AxiosPromise<Model.PlainTrackedObject<Investor>> {
        return this.axios.get(`${this.apiPath}/investor-lookup/${investorId}`);
    }

    public getInvestorLookupsPaged(request: Model.PartialPlainObject<Data.PageRequest<InvestorPagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<InvestorPagedLookup>>> {
        return this.axios.post(`${this.apiPath}/investors-paged`, request);
    }

    public getInvestorLookups(searchText: string): AxiosPromise<Array<Model.PlainObject<InvestorLookup>>> {
        return this.axios.get(`${this.apiPath}/lookups?searchText=${encodeURIComponent(searchText)}`);
    }

    public getInvestorLookup(investorId: number): AxiosPromise<Model.PlainObject<InvestorLookup>> {
        return this.axios.get(`${this.apiPath}/lookup/${investorId}`);
    }

    public saveInvestor(investor: Model.PartialPlainObject<Investor>): AxiosPromise<Model.PlainTrackedObject<Investor>> {
        return this.axios.post(`${this.apiPath}/save-investor`, investor);
    }

    // Client only properties / methods
}